import { Inbox } from 'lucide-react';

import type { CareStateInternalNoteT } from '@lotushealth/lotushealth_js_sdk/schemas';

import { CARE_STATE_SCOPE_LABEL, useCareStateDomain } from '@/entities/care-state-domain';
import { EmptyState } from '@/shared/ui/primitives';

function scopeLabelFromLinkType(note: CareStateInternalNoteT): string {
  switch (note.link.type) {
    case 'patient':
      return CARE_STATE_SCOPE_LABEL.patient;
    case 'problem':
      return CARE_STATE_SCOPE_LABEL.problem;
    case 'request':
      return CARE_STATE_SCOPE_LABEL.request;
    case 'orphan':
      return 'Orphan';
    case undefined:
      return 'Note';
    default: {
      const _exhaustive: never = note.link;
      return _exhaustive;
    }
  }
}

export function InternalNotesTab() {
  const { careState } = useCareStateDomain();
  if (careState === null) {
    return (
      <div className="flex h-full items-center justify-center p-4">
        <EmptyState
          icon={<Inbox className="h-10 w-10" />}
          title="Loading internal notes"
          description="Fetching notes for this patient."
        />
      </div>
    );
  }

  const notes = careState.internal_notes;

  if (notes.length === 0) {
    return (
      <div className="flex h-full items-center justify-center p-4">
        <EmptyState
          icon={<Inbox className="h-10 w-10" />}
          title="No internal notes"
          description="Internal notes for this patient will appear here."
        />
      </div>
    );
  }

  return (
    <div className="min-h-full p-4">
      <div className="space-y-2">
        {notes.map((note) => (
          <div key={note.id} className="rounded-md border bg-background px-3 py-2">
            <div className="text-xs font-medium text-muted-foreground">
              {scopeLabelFromLinkType(note)}
            </div>
            <div className="mt-1 whitespace-pre-wrap text-sm leading-snug text-foreground">
              {note.note.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
